import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X } from "lucide-react";
import { IMAGES, CONTACT } from "../../data/site";

const LINKS = [
  { label: "Story", href: "#story" },
  { label: "Menu", href: "#menu" },
  { label: "Quality", href: "#quality" },
  { label: "Branches", href: "#branches" },
  { label: "Visit", href: "#visit" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const target = href;
    const scroll = () => {
      const el = document.querySelector(target);
      if (!el) return;
      const lenis = window.__ssvpLenis;
      if (lenis && typeof lenis.scrollTo === "function") {
        lenis.scrollTo(el, { offset: -72, duration: 1.2 });
      } else {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    // defer so the mobile menu (AnimatePresence) can unmount first
    requestAnimationFrame(() => setTimeout(scroll, 60));
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-[100] transition-colors duration-500 ${
        scrolled
          ? "bg-[#141110]/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3 group"
        >
          <img
            src={IMAGES.logo}
            alt="Shree Siddhivinayak Vada Pav logo"
            className="h-11 w-11 rounded-full object-cover ring-1 ring-white/15 group-hover:ring-[#F59E0B]/60 transition-all duration-300"
          />
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-marathi-bold font-extrabold text-[#F8F4EC] text-lg group-hover:text-[#F59E0B] transition-colors duration-300">
              {CONTACT.nameMarathi}
            </span>
            <span className="font-marathi text-[11px] text-[#F59E0B] tracking-wide">
              {CONTACT.taglineMarathi}
            </span>
          </span>
        </button>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.href)}
              className="text-sm text-[#BFAFA6] hover:text-[#F8F4EC] transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-visit-cta"
            onClick={() => go("#visit")}
            className="px-5 py-2 rounded-full bg-[#F59E0B] text-[#141110] text-sm font-semibold hover:shadow-[0_0_28px_rgba(245, 158, 11,0.5)] hover:scale-[1.03] active:scale-95 transition-all duration-300"
          >
            Visit Us
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden text-[#F8F4EC] p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <MenuIcon size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="md:hidden overflow-hidden bg-[#141110]/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.href)}
                  className="text-left font-display text-3xl text-[#F8F4EC] hover:text-[#F59E0B] transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-cta"
                onClick={() => go("#visit")}
                className="mt-2 px-5 py-3 rounded-full bg-[#F59E0B] text-[#141110] font-semibold"
              >
                Visit Us
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
